import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export interface ContractField {
  name: string; // e.g., "mrr", "arr", "contractName", "metadata.originalDealAmount"
  label: string; // e.g., "Monthly Recurring Revenue (MRR)"
  type: "string" | "number" | "date" | "boolean";
  description?: string; // Human-readable description
  isRenewalSpecific?: boolean; // True if only for renewal deals
  isNested?: boolean; // True if nested field (e.g., metadata.*)
}

export interface FieldMapping {
  _id: string;
  organizationId: string;
  contractField: string; // e.g., "mrr", "arr"
  hubspotField: string; // e.g., "monthly_revenue"
  hubspotObjectType: "deal" | "company" | "contact";
  direction: "contract_to_hubspot"; // Always one-way
  status: boolean; // Active/inactive
  syncOnCreate: boolean; // Sync when contract is created
  syncOnUpdate: boolean; // Sync when contract is updated
  fieldType?: "string" | "number" | "date" | "boolean";
  transformation?: string; // Optional transformation function
  createdAt: string;
  updatedAt: string;
}

export interface HubSpotProperty {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  options?: Array<{
    label: string;
    value: string;
  }>;
  description?: string;
}

export interface CreateFieldMappingRequest {
  contractField: string; // Required - Contract field name (e.g., "mrr")
  hubspotField: string; // Required - HubSpot property name (e.g., "monthly_revenue")
  hubspotObjectType: "deal" | "company" | "contact"; // Required
  status?: boolean; // Optional - Default: true
  syncOnCreate?: boolean; // Optional - Default: true
  syncOnUpdate?: boolean; // Optional - Default: true
  fieldType?: "string" | "number" | "date" | "boolean"; // Optional
  transformation?: string; // Optional - JavaScript transformation function
}

export interface UpdateFieldMappingRequest extends Partial<CreateFieldMappingRequest> {}

export interface ContractFieldsResponse {
  contractFields: ContractField[];
}

export interface FieldMappingsResponse {
  mappings: FieldMapping[];
}

export interface HubSpotPropertiesResponse {
  properties: HubSpotProperty[];
}

export interface DeleteFieldMappingResponse {
  id: string;
}

export class FieldMappingService {
  /**
   * Get available contract fields that can be mapped
   */
  async getContractFields(
    config?: AxiosRequestConfig,
  ): Promise<{ data: ContractFieldsResponse; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<ContractFieldsResponse>
      >("/revsync/field-mappings/contract-fields", config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch contract fields.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Get writable HubSpot properties for a specific object type
   * Only returns writable fields - no need to filter on frontend
   */
  async getHubSpotProperties(
    objectType: "deal" | "company" | "contact",
    config?: AxiosRequestConfig,
  ): Promise<{ data: HubSpotPropertiesResponse; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<HubSpotPropertiesResponse>
      >("/revsync/field-mappings/properties", {
        ...config,
        params: { objectType },
      });

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch HubSpot properties.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Get all field mappings with optional filters
   */
  async getFieldMappings(
    filters?: {
      contractField?: string; // Filter by contract field name
      hubspotObjectType?: "deal" | "company" | "contact"; // Filter by HubSpot object type
      status?: boolean | string; // Filter by status ("true" or "false" as string, or boolean)
    },
    config?: AxiosRequestConfig,
  ): Promise<{ data: FieldMappingsResponse; message: string }> {
    try {
      const params: Record<string, string> = {};
      if (filters?.contractField) {
        params.contractField = filters.contractField;
      }
      if (filters?.hubspotObjectType) {
        params.hubspotObjectType = filters.hubspotObjectType;
      }
      if (filters?.status !== undefined) {
        params.status =
          typeof filters.status === "string"
            ? filters.status
            : String(filters.status);
      }

      const response = await httpService.get<
        ApiResponse<FieldMappingsResponse>
      >("/revsync/field-mappings", {
        ...config,
        params,
      });

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch field mappings.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Create a new field mapping
   * Validates that contractField exists and hubspotField is writable
   * Returns 409 error if duplicate mapping exists
   */
  async createFieldMapping(
    data: CreateFieldMappingRequest,
    config?: AxiosRequestConfig,
  ): Promise<{ data: { mapping: FieldMapping }; message: string }> {
    try {
      const response = await httpService.post<
        ApiResponse<{ mapping: FieldMapping }>
      >("/revsync/field-mappings", data, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to create field mapping.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing field mapping
   */
  async updateFieldMapping(
    id: string,
    data: Partial<CreateFieldMappingRequest>,
    config?: AxiosRequestConfig,
  ): Promise<{ data: { mapping: FieldMapping }; message: string }> {
    try {
      const response = await httpService.put<
        ApiResponse<{ mapping: FieldMapping }>
      >(`/revsync/field-mappings/${id}`, data, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update field mapping.";
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a field mapping
   */
  async deleteFieldMapping(
    id: string,
    config?: AxiosRequestConfig,
  ): Promise<{ data: DeleteFieldMappingResponse; message: string }> {
    try {
      const response = await httpService.delete<
        ApiResponse<DeleteFieldMappingResponse>
      >(`/revsync/field-mappings/${id}`, config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete field mapping.";
      throw new Error(errorMessage);
    }
  }
}

export const fieldMappingService = new FieldMappingService();
